import { create } from 'zustand';

/**
 * "A tela está ocupada porque o usuário PEDIU alguma coisa."
 *
 * Existe para separar dois carregamentos que o React Query não distingue,
 * mas que a pessoa distingue muito bem:
 *
 *  - **revalidação automática** (o dado passou do `staleTime`, a janela
 *    recebeu foco): ninguém pediu, ninguém está esperando, e um overlay aqui
 *    cobre uma tela que já está certa. Fica SILENCIOSA — ver o comentário do
 *    `CarregandoGlobal`.
 *  - **ação explícita** (clicar em Consultar, em Atualizar): a pessoa pediu e
 *    está olhando para a tela esperando resposta. Sem indicador, com a rede
 *    lenta, ela clica de novo ou conclui que travou.
 *
 * Portado do `ui-busy-store.ts` do isotec. Contador e não booleano: duas
 * ações concorrentes (Consultar enquanto o Atualizar ainda responde) fariam a
 * primeira a terminar apagar o indicador da outra.
 */
interface EstadoOcupado {
  /** Quantas ações do usuário estão em voo. */
  ocupado: number;
  /** O que dizer enquanto isso. `null` cai no texto padrão. */
  rotulo: string | null;
  abrir: (rotulo?: string) => void;
  fechar: () => void;
}

export const useOcupado = create<EstadoOcupado>((set) => ({
  ocupado: 0,
  rotulo: null,
  abrir: (rotulo) => set((e) => ({ ocupado: e.ocupado + 1, rotulo: rotulo ?? e.rotulo })),
  fechar: () =>
    set((e) => {
      const ocupado = Math.max(0, e.ocupado - 1);
      // O rótulo cai junto com a última ação: mantê-lo faria a próxima espera
      // herdar a legenda da anterior.
      return { ocupado, rotulo: ocupado === 0 ? null : e.rotulo };
    }),
}));

/**
 * Roda uma ação do usuário com o indicador global aceso, e o apaga no fim —
 * inclusive se ela falhar.
 *
 * `finally` e não `then`: sem ele, um erro de rede deixa o indicador aceso
 * para sempre, e aí a tela fica de fato travada — pelo próprio aviso de que
 * não estava.
 */
export async function comEspera<T>(acao: () => Promise<T>, rotulo?: string): Promise<T> {
  const { abrir, fechar } = useOcupado.getState();
  abrir(rotulo);
  try {
    return await acao();
  } finally {
    fechar();
  }
}
